import React, { useState, useEffect } from 'react';
import { collection, getDocs, query, orderBy, limit } from 'firebase/firestore';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { db } from '../../firebase/config';
import {
    Cloud, RefreshCw, CheckCircle2, AlertCircle, Loader,
    DollarSign, Building2, Siren, Database,
} from 'lucide-react';

/**
 * 공공기관 데이터 통합 동기화
 *  - HIRA 비급여 진료비 (분기 자동)
 *  - HIRA 의료기관 상세 (배치 보강)
 *  - 응급의료기관 (분기 자동)
 */
function AdminPublicDataSync() {
    const [stats, setStats] = useState({
        hiraPrices: 0, hospitalsEnriched: 0, emergencyHospitals: 0,
        nhisStats: 0,
    });
    const [recentLogs, setRecentLogs] = useState([]);
    const [running, setRunning] = useState(null); // 'nonpayment' | 'detail' | 'emergency'
    const [results, setResults] = useState({});
    const [error, setError] = useState(null);

    useEffect(() => { loadStats(); loadLogs(); }, []);

    const loadStats = async () => {
        try {
            const [hira, hospitals, emergency, nhis] = await Promise.all([
                getDocs(collection(db, 'hiraAveragePrices')),
                getDocs(query(collection(db, 'hospitals'), limit(500))),
                getDocs(collection(db, 'emergencyHospitals')),
                getDocs(collection(db, 'nhisRegionalStats')),
            ]);
            const enrichedCount = hospitals.docs.filter(d => d.data().hiraDetailSyncedAt).length;
            setStats({
                hiraPrices: hira.size,
                hospitalsEnriched: enrichedCount,
                emergencyHospitals: emergency.size,
                nhisStats: nhis.size,
            });
        } catch (_) {}
    };

    const loadLogs = async () => {
        try {
            const snap = await getDocs(query(
                collection(db, 'syncLogs'),
                orderBy('finishedAt', 'desc'),
                limit(20),
            ));
            setRecentLogs(snap.docs.map(d => ({ id: d.id, ...d.data() })));
        } catch (_) {}
    };

    const runSync = async (key, fnName, confirmMsg, args = {}) => {
        if (!window.confirm(confirmMsg)) return;
        setRunning(key);
        setError(null);
        setResults(prev => ({ ...prev, [key]: null }));
        try {
            const fns = getFunctions(undefined, 'asia-northeast3');
            // 9분 timeout — Cloud Function 의 540s 와 맞춤 (분기 동기화는 시간 오래 걸림)
            const fn = httpsCallable(fns, fnName, { timeout: 540000 });
            const res = await fn(args);
            setResults(prev => ({ ...prev, [key]: res.data }));
            await loadStats();
            await loadLogs();
        } catch (e) {
            setError(`${key} 실패: ${e.message}`);
        }
        setRunning(null);
    };

    return (
        <div style={S.page}>
            <div style={S.header}>
                <h1 style={S.title}>
                    <Cloud size={22} style={{ verticalAlign: 'middle', marginRight: 8 }} />
                    공공기관 데이터 동기화
                </h1>
                <p style={S.subtitle}>
                    국민건강보험공단·건강보험심사평가원·국립중앙의료원 OpenAPI 데이터를 자동 동기화합니다.
                    각 항목은 분기·배치 단위 자동 실행되며, 관리자가 즉시 수동 트리거할 수도 있습니다.
                </p>
            </div>

            {/* 통계 */}
            <div style={S.statsRow}>
                <Stat icon={DollarSign} color="#0ea5e9" label="HIRA 비급여 평균" value={stats.hiraPrices} />
                <Stat icon={Building2} color="#10b981" label="치과 상세 보강" value={stats.hospitalsEnriched} />
                <Stat icon={Siren} color="#ef4444" label="응급의료기관" value={stats.emergencyHospitals} />
                <Stat icon={Database} color="#f59e0b" label="NHIS 시도 통계" value={stats.nhisStats} />
            </div>

            {error && <div style={S.error}><AlertCircle size={14} /> {error}</div>}

            {/* 동기화 카드들 */}
            <SyncCard
                title="HIRA 비급여 진료비"
                description="전국 17개 시도 치과의원·치과병원의 비급여 가격 → 광역시·도 평균 집계"
                icon={DollarSign}
                color="#0ea5e9"
                schedule="분기 자동 (1·4·7·10월 1일 03:00)"
                running={running === 'nonpayment'}
                result={results.nonpayment}
                onClick={() => runSync(
                    'nonpayment', 'adminSyncHiraNonpayment',
                    '전국 17개 시도 치과 비급여 데이터 동기화 시작?\n약 5~10분 소요됩니다.',
                )}
            />

            <SyncCard
                title="HIRA 의료기관 상세 보강"
                description="hospitals 컬렉션의 의료장비·진료과목·전문의·교통 정보 자동 채움 (50개씩 배치)"
                icon={Building2}
                color="#10b981"
                schedule="수동 (배치당 50개)"
                running={running === 'detail'}
                result={results.detail}
                onClick={() => runSync(
                    'detail', 'adminSyncHiraDetail',
                    '치과 50개 상세정보를 보강합니다 (약 30초~1분).\n총 19,000개 보강에는 분할 실행 필요.',
                    { batchSize: 50 },
                )}
            />

            <SyncCard
                title="응급의료기관 정보"
                description="국립중앙의료원 응급의료기관 → 야간·주말 운영 안내용"
                icon={Siren}
                color="#ef4444"
                schedule="분기 자동 (1·4·7·10월 1일 04:00)"
                running={running === 'emergency'}
                result={results.emergency}
                onClick={() => runSync(
                    'emergency', 'adminSyncEmergencyHospitals',
                    '전국 응급의료기관 정보를 동기화합니다 (약 3~5분).',
                )}
            />

            {/* 최근 동기화 이력 */}
            {recentLogs.length > 0 && (
                <div style={S.section}>
                    <h3 style={S.sectionTitle}>최근 동기화 이력</h3>
                    <table style={S.table}>
                        <thead>
                            <tr>
                                <th style={S.th}>시점</th>
                                <th style={S.th}>유형</th>
                                <th style={S.th}>처리 결과</th>
                            </tr>
                        </thead>
                        <tbody>
                            {recentLogs.map(log => (
                                <tr key={log.id} style={S.tr}>
                                    <td style={S.td}>{log.finishedAt?.toDate ? new Date(log.finishedAt.toDate()).toLocaleString('ko-KR') : '-'}</td>
                                    <td style={S.td}>{labelType(log.type)}</td>
                                    <td style={S.td}>{summarizeLog(log)}</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                </div>
            )}
        </div>
    );
}

function labelType(t) {
    return {
        hira_nonpayment: 'HIRA 비급여',
        hira_detail: 'HIRA 의료기관 상세',
        emergency_hospitals: '응급의료기관',
    }[t] || t;
}

function summarizeLog(log) {
    if (log.type === 'hira_nonpayment') {
        const ok = (log.results || []).filter(r => r.ok).length;
        return `${ok} / ${log.results?.length || 0} 시도 처리 (${log.quarter || ''})`;
    }
    if (log.type === 'hira_detail') {
        return `${log.processed || 0}개 보강 / 성공 ${log.totalSuccess || 0}건`;
    }
    if (log.type === 'emergency_hospitals') {
        return `${log.totalSaved || 0}개 응급기관 저장`;
    }
    return JSON.stringify(log).substring(0, 80);
}

function Stat({ icon: Icon, color, label, value }) {
    return (
        <div style={S.statCard}>
            <Icon size={20} color={color} />
            <div style={{ ...S.statNum, color }}>{value.toLocaleString('ko-KR')}</div>
            <div style={S.statLabel}>{label}</div>
        </div>
    );
}

function SyncCard({ title, description, icon: Icon, color, schedule, running, result, onClick }) {
    return (
        <div style={S.section}>
            <div style={{ display: 'flex', alignItems: 'flex-start', gap: 12 }}>
                <div style={{ ...S.iconBox, background: color + '22' }}>
                    <Icon size={20} color={color} />
                </div>
                <div style={{ flex: 1 }}>
                    <h3 style={S.sectionTitle}>{title}</h3>
                    <p style={S.sectionDesc}>{description}</p>
                    <div style={{ fontSize: 11, color: '#94a3b8', marginBottom: 10 }}>📅 {schedule}</div>
                    <button onClick={onClick} disabled={running} style={{ ...S.runBtn, background: color }}>
                        {running ? <Loader size={14} /> : <RefreshCw size={14} />}
                        {running ? '동기화 중…' : '지금 동기화'}
                    </button>
                    {result && (
                        <div style={S.success}>
                            <CheckCircle2 size={14} />
                            <strong>완료:</strong> {summarizeResult(title, result)}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}

function summarizeResult(title, result) {
    if (result.processed != null) {
        return `${result.processed}개 처리 (성공 ${result.totalSuccess || 0})`;
    }
    if (result.totalSaved != null) {
        return `${result.totalSaved}개 저장`;
    }
    if (result.results) {
        return `${result.results.length}개 시도 처리`;
    }
    return JSON.stringify(result).substring(0, 100);
}

const S = {
    page: { maxWidth: 1100, margin: '0 auto', padding: 24 },
    header: { marginBottom: 16 },
    title: { fontSize: 22, fontWeight: 900, margin: 0, color: '#0f172a' },
    subtitle: { fontSize: 13, color: '#64748b', marginTop: 6, lineHeight: 1.7 },
    statsRow: { display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 10, marginBottom: 18 },
    statCard: {
        background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12, padding: 14,
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 4,
    },
    statNum: { fontSize: 22, fontWeight: 900 },
    statLabel: { fontSize: 11, color: '#64748b' },
    section: { marginBottom: 12, padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 12 },
    sectionTitle: { fontSize: 15, fontWeight: 800, marginBottom: 4, color: '#0f172a' },
    sectionDesc: { fontSize: 12, color: '#64748b', marginBottom: 6, lineHeight: 1.6 },
    iconBox: {
        width: 40, height: 40, borderRadius: 10, flexShrink: 0,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    },
    runBtn: {
        display: 'inline-flex', alignItems: 'center', gap: 6,
        padding: '8px 14px', color: '#fff', border: 'none',
        borderRadius: 8, fontSize: 12, fontWeight: 800, cursor: 'pointer',
    },
    error: { padding: 12, marginBottom: 12, background: '#fee2e2', color: '#991b1b', borderRadius: 8, fontSize: 13, display: 'flex', alignItems: 'center', gap: 6 },
    success: { padding: 10, marginTop: 10, background: '#d1fae5', color: '#065f46', borderRadius: 6, fontSize: 12, display: 'flex', alignItems: 'center', gap: 6 },
    table: { width: '100%', borderCollapse: 'collapse', fontSize: 12 },
    th: { textAlign: 'left', padding: 8, background: '#f8fafc', borderBottom: '1px solid #e2e8f0', fontWeight: 800, color: '#64748b', fontSize: 11 },
    tr: { borderBottom: '1px solid #f1f5f9' },
    td: { padding: 8 },
};

export default AdminPublicDataSync;
